import { useEffect, useState, useCallback } from "react";
import { Search, X, Mail, Phone, MapPin, Clock, Download, Plus, CalendarPlus, ShieldAlert, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ConflictCheckModal from "@/admin/ConflictCheckModal";

const STAGES = ["New", "Contacted", "Triaged", "Consultation Booked", "Converted", "Closed"];
const SOURCES = ["website", "phone", "email", "referral", "walk-in", "manual"];
const CHANNELS = ["Phone", "Video", "In person", "WhatsApp"];
const badge = (s) => ({
  New: "bg-gold/15 text-gold", Contacted: "bg-blue-100 text-blue-700", Triaged: "bg-indigo-100 text-indigo-700",
  Booked: "bg-emerald-100 text-emerald-700", "Consultation Booked": "bg-emerald-100 text-emerald-700",
  Converted: "bg-purple-100 text-purple-700", Closed: "bg-slate-200 text-slate-600",
}[s] || "bg-slate-100 text-slate-600");
const fmt = (d) => { try { return new Date(d).toLocaleString("en-GB"); } catch { return d; } };
const EMPTY = { type: "enquiry", name: "", email: "", phone: "", service_area: "", summary: "", source: "manual", preferred_contact: "" };

export default function EnquiriesManager() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [type, setType] = useState("all");
  const [source, setSource] = useState("all");
  const [active, setActive] = useState(null);
  const [dups, setDups] = useState([]);
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [booking, setBooking] = useState(false);
  const [bookForm, setBookForm] = useState({ scheduled_at: "", channel: "Video", adviser: "", fee_status: "Unpaid" });
  const [conflict, setConflict] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (type !== "all") params.type = type;
    if (status !== "all") params.status = status;
    if (source !== "all") params.source = source;
    if (q) params.q = q;
    api.get("/admin/enquiries", { params }).then((r) => setItems(r.data)).catch(() => {});
  }, [type, status, source, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {}); }, []);

  const open = async (e) => {
    setActive(e); setBooking(false); setDups([]);
    try { const { data } = await api.get(`/admin/enquiries/${e.id}/duplicates`); setDups(data); } catch { /* */ }
  };

  const patch = async (payload) => {
    const { data } = await api.patch(`/admin/enquiries/${active.id}`, payload);
    setActive(data); load();
    return data;
  };
  const updateStatus = (s) => patch({ status: s });
  const addNote = async () => { if (!note.trim()) return; await patch({ note }); setNote(""); toast.success("Note added"); };

  const submitCreate = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/enquiries", form);
      if (data.duplicates?.length) toast.warning(`${data.duplicates.length} possible duplicate(s) found for this contact.`);
      else toast.success("Enquiry created");
      setCreating(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const bookConsultation = async () => {
    try {
      await api.post(`/admin/enquiries/${active.id}/convert-to-consultation`, bookForm);
      toast.success("Consultation booked — see Consultations");
      setBooking(false); await open({ ...active });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const exportCsv = () => {
    const rows = [["Date", "Type", "Name", "Email", "Phone", "Area", "Source", "Status", "Summary"]];
    items.forEach((e) => rows.push([fmt(e.created_at), e.type, e.name, e.email, e.phone || "", e.service_area || "", e.source || "", e.status, (e.summary || "").replace(/\n/g, " ")]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "enquiries.csv"; a.click();
  };

  return (
    <div data-testid="enquiries-manager">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold text-navy">Enquiries &amp; Intake</h1>
          <p className="mt-1 text-sm text-slate-500">{items.length} record(s) · every inbound lead from the website and manual entry.</p>
        </div>
        <div className="flex gap-2">
          {can("enquiries", "create") && <button onClick={() => { setForm(EMPTY); setCreating(true); }} data-testid="new-enquiry-btn" className="btn-gold text-sm"><Plus size={15} /> New enquiry</button>}
          <button onClick={exportCsv} className="btn-navy text-sm"><Download size={15} /> Export CSV</button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, area…" className="pl-9" data-testid="enquiry-search" />
        </div>
        <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="enquiry-type-filter">
          <option value="all">All types</option><option value="enquiry">Enquiry</option><option value="consultation">Consultation request</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="enquiry-status-filter">
          <option value="all">All statuses</option>{STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={source} onChange={(e) => setSource(e.target.value)} className="rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="enquiry-source-filter">
          <option value="all">All sources</option>{SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr><th className="px-5 py-3">Name</th><th className="px-5 py-3 hidden sm:table-cell">Area</th><th className="px-5 py-3 hidden lg:table-cell">Source</th><th className="px-5 py-3 hidden md:table-cell">Date</th><th className="px-5 py-3">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((e) => (
              <tr key={e.id} data-testid={`enquiry-row-${e.id}`} onClick={() => open(e)} className="cursor-pointer hover:bg-slate-50">
                <td className="px-5 py-3"><div className="font-medium text-navy">{e.name}{e.type === "consultation" && <span className="ml-2 rounded bg-navy/5 px-1.5 py-0.5 text-[10px] uppercase text-navy">consult req</span>}</div><div className="text-xs text-slate-400">{e.email}</div></td>
                <td className="px-5 py-3 hidden sm:table-cell text-slate-600">{e.service_area || "—"}</td>
                <td className="px-5 py-3 hidden lg:table-cell text-slate-500 capitalize">{e.source || "website"}</td>
                <td className="px-5 py-3 hidden md:table-cell text-xs text-slate-500">{fmt(e.created_at)}</td>
                <td className="px-5 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${badge(e.status)}`}>{e.status}</span></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-400">No records.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Detail drawer */}
      {active && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setActive(null)} />
          <div className="relative flex h-full w-full max-w-lg flex-col bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <h2 className="font-serif text-xl font-semibold text-navy">{active.name}</h2>
              <button onClick={() => setActive(null)} className="text-slate-400 hover:text-navy"><X size={20} /></button>
            </div>
            <div className="flex-1 space-y-5 overflow-y-auto p-6">
              {dups.length > 0 && (
                <div data-testid="duplicate-warning" className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <div><strong>{dups.length} possible duplicate(s)</strong> share this email/phone: {dups.map((d) => d.name).join(", ")}.</div>
                </div>
              )}
              {active.consultation_id && <div className="rounded-lg bg-emerald-50 p-3 text-xs text-emerald-700">A consultation has been booked from this enquiry.</div>}

              {can("enquiries", "edit") && (
                <div className="flex flex-wrap gap-2">
                  {STAGES.map((s) => (
                    <button key={s} onClick={() => updateStatus(s)} data-testid={`enquiry-set-${s.replace(/\s/g, "-")}`}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium ${active.status === s ? badge(s) + " ring-2 ring-offset-1 ring-navy/20" : "bg-slate-100 text-slate-500"}`}>{s}</button>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <button onClick={() => setConflict(true)} data-testid="run-conflict-check-btn" className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-xs font-medium text-navy hover:bg-slate-50"><ShieldAlert size={14} /> Conflict check</button>
                {can("consultations", "create") && !active.consultation_id && (
                  <button onClick={() => setBooking((b) => !b)} data-testid="book-consultation-btn" className="flex items-center gap-1.5 rounded-lg bg-navy px-3 py-2 text-xs font-medium text-white hover:bg-navy-light"><CalendarPlus size={14} /> Book consultation</button>
                )}
              </div>

              {booking && (
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="mb-2 text-xs font-semibold uppercase text-slate-400">Schedule consultation</p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <div><Label className="text-xs">Date &amp; time</Label><Input type="datetime-local" data-testid="book-datetime" value={bookForm.scheduled_at} onChange={(e) => setBookForm({ ...bookForm, scheduled_at: e.target.value })} className="mt-1" /></div>
                    <div><Label className="text-xs">Channel</Label><select data-testid="book-channel" value={bookForm.channel} onChange={(e) => setBookForm({ ...bookForm, channel: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">{CHANNELS.map((c) => <option key={c}>{c}</option>)}</select></div>
                    <div><Label className="text-xs">Adviser</Label><select data-testid="book-adviser" value={bookForm.adviser} onChange={(e) => setBookForm({ ...bookForm, adviser: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"><option value="">Unassigned</option>{staff.map((s) => <option key={s.email} value={s.email}>{s.name}</option>)}</select></div>
                    <div><Label className="text-xs">Fee status</Label><select data-testid="book-fee" value={bookForm.fee_status} onChange={(e) => setBookForm({ ...bookForm, fee_status: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">{["Unpaid", "Paid", "Waived", "N/A"].map((f) => <option key={f}>{f}</option>)}</select></div>
                  </div>
                  <button onClick={bookConsultation} data-testid="confirm-booking-btn" className="btn-gold mt-3 w-full text-sm">Confirm booking</button>
                </div>
              )}

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-600"><Mail size={15} className="text-gold" /> {active.email}</div>
                {active.phone && <div className="flex items-center gap-2 text-slate-600"><Phone size={15} className="text-gold" /> {active.phone}</div>}
                {active.location && <div className="flex items-center gap-2 text-slate-600"><MapPin size={15} className="text-gold" /> {active.location}</div>}
                {active.preferred_contact && <div className="text-slate-600"><span className="text-slate-400">Preferred contact:</span> {active.preferred_contact}</div>}
                {active.preferred_time && <div className="flex items-center gap-2 text-slate-600"><Clock size={15} className="text-gold" /> {active.preferred_time}</div>}
                <div className="text-slate-600"><span className="text-slate-400">Service area:</span> {active.service_area || "—"}</div>
                <div className="text-slate-600 capitalize"><span className="text-slate-400">Source:</span> {active.source || "website"}</div>
              </div>
              <div>
                <div className="mb-1 text-xs font-semibold uppercase text-slate-400">Summary</div>
                <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-700">{active.summary}</p>
              </div>
              {can("enquiries", "assign") && (
                <div>
                  <div className="mb-1 text-xs font-semibold uppercase text-slate-400">Assigned to</div>
                  <select data-testid="enquiry-assign" value={active.assigned_to || ""} onChange={(e) => patch({ assigned_to: e.target.value })} className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
                    <option value="">Unassigned</option>{staff.map((s) => <option key={s.email} value={s.email}>{s.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <div className="mb-2 text-xs font-semibold uppercase text-slate-400">Internal notes</div>
                <div className="space-y-2">
                  {(active.notes || []).map((n, i) => (
                    <div key={i} className="rounded-lg border border-slate-100 bg-slate-50 p-2.5 text-xs">
                      <div className="text-slate-700">{n.text}</div><div className="mt-1 text-slate-400">{n.by} · {fmt(n.at)}</div>
                    </div>
                  ))}
                </div>
                {can("enquiries", "edit") && <><Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" className="mt-2" rows={2} data-testid="enquiry-note-input" />
                <button onClick={addNote} className="btn-navy mt-2 text-xs">Add note</button></>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create modal */}
      {creating && (
        <div className="fixed inset-0 z-[55] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCreating(false)} />
          <div className="relative z-10 max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between"><h2 className="font-serif text-xl font-semibold text-navy">New enquiry</h2><button onClick={() => setCreating(false)} className="text-slate-400"><X size={18} /></button></div>
            <form onSubmit={submitCreate} className="space-y-3">
              <div><Label>Name</Label><Input data-testid="create-enquiry-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input data-testid="create-enquiry-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1" /></div>
                <div><Label>Phone</Label><Input data-testid="create-enquiry-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Source</Label><select data-testid="create-enquiry-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">{SOURCES.map((s) => <option key={s}>{s}</option>)}</select></div>
                <div><Label>Service area</Label><Input data-testid="create-enquiry-area" value={form.service_area} onChange={(e) => setForm({ ...form, service_area: e.target.value })} className="mt-1" /></div>
              </div>
              <div><Label>Summary</Label><Textarea data-testid="create-enquiry-summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} required rows={3} className="mt-1" /></div>
              <button type="submit" data-testid="create-enquiry-submit" className="btn-gold w-full">Create enquiry</button>
            </form>
          </div>
        </div>
      )}

      {conflict && active && (
        <ConflictCheckModal subject={{ name: active.name, email: active.email, phone: active.phone }}
          linkedType="enquiry" linkedId={active.id} onClose={() => setConflict(false)} />
      )}
    </div>
  );
}
